import {
  isResultFieldReviewed,
  resultFieldValue,
  type DocumentResultField,
  type EvaluationDocument,
  type FieldType,
  type TemplateField,
} from '@/types/domain'

/**
 * Turning documents into table rows.
 *
 * Everything in this file is pure so that the joins can be tested without a
 * click: which column a value belongs in, which cell is flagged, and what a
 * checkbox result means are all decisions a person would otherwise have to
 * find by reading a rendered table.
 */

/** A shared template field as `GET /v1/templates/:id/fields` reports it: the
 *  column, plus where its box sits on each layout. */
export type RecordColumn = TemplateField & {
  placements: Array<{ page: number; boxId: string }>
}

/** Checkbox and signature fields are a yes/no, not a string. */
export type BinaryState = 'checked' | 'unchecked' | 'unknown'

export interface RecordCell {
  /** False when the document's matched layout carries no box for this field
   *  — a 2019 form has no cell for a question only the 2018 form asked. That
   *  is not an empty value, and it is not something to review. */
  present: boolean
  /** The delivered text, through `resultFieldValue`. */
  value: string
  /** What the recognizer read, kept apart from `value` so a correction is
   *  visible as one. */
  text: string
  confidence: number
  reviewed: boolean
  /** Waiting for a person. Never true on a reviewed cell: somebody decided. */
  flagged: boolean
  /** Checkbox and signature fields render a mark, not their raw text. */
  binary?: BinaryState
}

export const EMPTY_CELL: RecordCell = {
  present: false,
  value: '',
  text: '',
  confidence: 0,
  reviewed: false,
  flagged: false,
}

export interface RecordRow {
  documentId: string
  /** The uploaded file's name. Not a column in the design; it is the row's
   *  identity for search and for the review link's accessible name. */
  title: string
  matchedPage?: number | null
  cells: Record<string, RecordCell>
  /** How many fields this reading left for a person. From the arm's own
   *  variant when one is on screen, so the column says what *that* reading
   *  thought — not what the delivered values think. */
  needsReviewCount: number
  reviewed: boolean
  /** True while an arm is selected and this document has no variant for it:
   *  the row shows the delivered values instead of blanks, which has to be
   *  said or the table quietly mixes two readings. */
  fallbackToDelivered: boolean
}

/** Mirrors `interpretCheckboxState` in the Flutter app: the pipeline writes a
 *  checkbox result in whatever form the analyzer produced, so the common
 *  spellings are normalised and anything else stays undecided rather than
 *  being read as "no". */
const CHECKED = new Set([
  'true', '1', 'x', 'yes', 'y', 'ja', 'checked', 'angekreuzt', 'gesetzt', 'an', '✓', '✔',
])
const UNCHECKED = new Set([
  'false', '0', 'no', 'n', 'nein', 'unchecked', 'nicht angekreuzt', 'leer', 'aus',
])

export function binaryState(text: string): BinaryState {
  const value = text.trim().toLowerCase()
  if (value === '') return 'unknown'
  if (CHECKED.has(value)) return 'checked'
  if (UNCHECKED.has(value)) return 'unchecked'
  return 'unknown'
}

export const BINARY_LABELS: Record<BinaryState, string> = {
  checked: 'angekreuzt',
  unchecked: 'nicht angekreuzt',
  unknown: 'unklar',
}

export function isBinaryField(type: FieldType): boolean {
  return type === 'checkBox' || type === 'radioBox' || type === 'signature'
}

/**
 * Whether a cell is waiting for a person.
 *
 * `needsReview` is the analyzer's own judgement and is authoritative where it
 * exists. Where it does not — results written before the flag — the only
 * honest substitute is the template's confidence threshold, which is what the
 * review screens use. A reviewed cell is never flagged.
 */
export function cellFlagged(
  field: DocumentResultField,
  threshold: number,
  reviewed: boolean,
): boolean {
  if (reviewed) return false
  if (typeof field.needsReview === 'boolean') return field.needsReview
  return (field.confidence ?? 0) < threshold
}

/**
 * The box that carries `column` on the layout `matchedPage` names.
 *
 * Page numbers are never compacted, so this is a lookup by number and never
 * an index — the same rule `pageByNumber` exists for.
 */
export function boxIdFor(column: RecordColumn, matchedPage?: number | null): string | undefined {
  if (matchedPage == null) return undefined
  return column.placements.find((placement) => placement.page === matchedPage)?.boxId
}

/**
 * One document as a row, joined against the template's shared fields.
 *
 * The join runs through the field's `placements` because a document's
 * `result` is keyed by **box id**: the same field is a different box on each
 * layout, and keying the table off whatever the first document happened to
 * contain would produce a table whose shape changes as you page.
 */
export function buildRecordRow(
  doc: EvaluationDocument,
  columns: RecordColumn[],
  threshold: number,
  variant?: { result?: Record<string, DocumentResultField>; needsReviewCount?: number },
): RecordRow {
  // A variant with no result map is "the arm never ran on this document",
  // not "the arm read nothing": the row falls back to the delivered values
  // and says so, rather than showing a screen full of blanks.
  const armResult = variant?.result
  const usingArm = armResult != null && Object.keys(armResult).length > 0
  const result = usingArm ? armResult : doc.result

  const cells: Record<string, RecordCell> = {}
  let reviewed = false

  for (const column of columns) {
    const boxId = boxIdFor(column, doc.matchedPage)
    const field = boxId ? result?.[boxId] : undefined
    if (!boxId || !field) {
      cells[column.id] = EMPTY_CELL
      continue
    }
    const value = resultFieldValue(field)
    const isReviewed = isResultFieldReviewed(field)
    if (isReviewed) reviewed = true
    cells[column.id] = {
      present: true,
      value,
      text: field.text ?? '',
      confidence: field.confidence ?? 0,
      reviewed: isReviewed,
      flagged: cellFlagged(field, threshold, isReviewed),
      binary: isBinaryField(column.type) ? binaryState(value) : undefined,
    }
  }

  return {
    documentId: doc.id,
    title: doc.title ?? '',
    matchedPage: doc.matchedPage,
    cells,
    needsReviewCount: usingArm
      ? (variant?.needsReviewCount ?? 0)
      : (doc.needsReviewCount ?? 0),
    reviewed,
    fallbackToDelivered: variant !== undefined && !usingArm,
  }
}

/** Only analysed documents are records: a `neu` or `noMatch` document has no
 *  values to put in a row. */
export function isRecordDocument(doc: EvaluationDocument): boolean {
  return doc.result != null && Object.keys(doc.result).length > 0
}

/**
 * The search box's filter, over the file name and every cell of the loaded
 * rows. It cannot be more than that: `GET /documents` has no `q`, so the
 * field is labelled "in den geladenen Datensätzen" rather than promising a
 * search of the whole template.
 */
export function matchesQuery(row: RecordRow, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (q === '') return true
  if (row.title.toLowerCase().includes(q)) return true
  return Object.values(row.cells).some(
    (cell) =>
      cell.value.toLowerCase().includes(q) || cell.text.toLowerCase().includes(q),
  )
}

/** Every loaded document as a row, in load order. */
export function buildRecordRows(
  docs: EvaluationDocument[],
  columns: RecordColumn[],
  threshold: number,
  variants?: Map<string, { result?: Record<string, DocumentResultField>; needsReviewCount?: number }>,
): RecordRow[] {
  return docs
    .filter(isRecordDocument)
    .map((doc) =>
      buildRecordRow(
        doc,
        columns,
        threshold,
        variants ? (variants.get(doc.id) ?? {}) : undefined,
      ),
    )
}
